import styles from '@/styles/checkout.module.scss'
import { Button, Form, Input, Space, Select, message, Table, TableColumnsType, Checkbox, ConfigProvider } from 'antd';
import { useState, useEffect } from 'react';
import myAccountApi from '@/service/modules/myAccountApi'
import { PlusOutlined, UpOutlined, WarningFilled } from '@ant-design/icons';
import loginApi from '@/service/modules/loginApi';
import _ from 'lodash';
import { matomoSetCustomDimension } from "@/utils/matomoEvent"

interface Address {
  address: string,
  address1: string,
  addressID: number,
  firstName: string,
  lastName: string,
  phone: string,
  postCode: string,
  suburb: string,
  state: number
}

interface AddressFormProps {
  address: Address | null
  success: any
  cancel: any
  type?: 'add' | 'edit'
  email?: string
  subscriptionEmail?: boolean
  editType?: 'shipping' | 'billing'
  addressList?: any[]
  setAddressInfo?: Function
  defaultFormData?: any
  canEdit?: boolean
}

interface DataType {
  key: React.Key;
  name: string;
  age: number;
  address: string;
}


export default function AddressForm({ address, success, cancel, type, email, subscriptionEmail, editType, canEdit, addressList, defaultFormData, setAddressInfo }: AddressFormProps) {
  const [form] = Form.useForm();
  const { Option } = Select

  interface suburbOptionsItem {
    subID: number,
    subName: string,
    zoneID: string,
    zoneCode: string,
    zoneName: string,
    countryID: number
  }
  const [suburbOptions, setSuburbOptions] = useState<suburbOptionsItem[]>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [showSelectAddress, setShowSelectAddress] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [addAddressType, setAddAddressType] = useState<'add' | 'edit'>(type || "add");
  const [needBack, setNeedBack] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  const columns: TableColumnsType<DataType> = [
    {
      width: 150,
      title: 'Receiver Name',
      dataIndex: 'firstName',
      render(value: any, record: any) {
        return (
          <div onClick={() => {
            const key = [record.addressID]
            changeAddress(key, true)
            setSelectedRowKeys(key)
          }} style={{ cursor: "pointer" }}>
            {value}&nbsp;{record.lastName}
          </div>
        )
      },
    },
    {
      title: 'Street/Address',
      dataIndex: 'address',
      render(value, record) {
        return (
          <>
            {value}
          </>
        )
      },
    },
    {
      title: 'Suburb',
      dataIndex: 'city',
    },
    {
      title: 'Postcode',
      dataIndex: 'postCode',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
    }
  ];

  // 设置默认值
  useEffect(() => {
    if (defaultFormData) {
      setTimeout(() => {
        getSuburbOptions(defaultFormData.postCode, false)
        const formValue = {
          ...defaultFormData,
          suburb: defaultFormData.city,
          isDefaultAddress: defaultFormData.isShippingAddress == 1 ? true : false,
          isDefaultBillingAddress: defaultFormData.isBillingAddress == 1 ? true : false
        }
        form.setFieldsValue(formValue)
        setInitialValues({
          address: formValue.address,
          address1: formValue.address1,
          firstName: formValue.firstName,
          lastName: formValue.lastName,
          phone: formValue.phone,
          postCode: formValue.postCode,
          suburb: formValue.city,
          state: formValue.state,
          isDefaultAddress: formValue.isDefaultAddress,
          isDefaultBillingAddress: formValue.isDefaultBillingAddress,
          country: undefined
        })
        setNeedBack(true)
        setAddAddressType('edit');
        setShowSelectAddress(false);
        if (selectedRowKeys[0] != defaultFormData.addressID) {
          setSelectedRowKeys([defaultFormData.addressID])
        }

        if (defaultFormData.postCode) {
          form.validateFields()
        }
      }, 100);
    }

  }, [defaultFormData])

  // postCode数值变化
  const changePostCode = () => {
    const postCode = form.getFieldsValue().postCode

    // 判断合规
    if (postCode.length != 4) return
    if (!postCode.match(/^\d{4}$/)) return

    // 请求suburb数据
    getSuburbOptions(postCode, true)
    console.log(true)
  }

  // Suburb数值变化
  const handleSuburbChange = () => {
    const suburb = form.getFieldsValue().suburb
    const target = suburbOptions.filter(item => item.subName === suburb)

    // 给state赋值
    if (target.length) {
      form.setFieldsValue({
        state: target[0].zoneCode
      })
    }
  }

  // 获取suburb state
  const getSuburbOptions = async (postCode: string, defaultAssign?: boolean) => {
    try {
      let res = await myAccountApi.getSubAndStatByPostcode({ postCode: postCode })
      const suburbList = res.data
      if (suburbList && suburbList.length) {
        setSuburbOptions(suburbList)

        // postCode更改的触发，Suburb取第一条为默认值
        if (defaultAssign) {
          form.setFieldsValue({
            suburb: suburbList[0].subName,
            state: suburbList[0].zoneCode
          })
        } else {
          const suburbObj = suburbList.find((item:any) => item.subName === form.getFieldsValue().suburb)
          if (!suburbObj) {
            // 数据对不上，设置默认第一条
            form.setFieldsValue({
              suburb: suburbList[0].subName,
              state: suburbList[0].zoneCode
            })
          }          
        }
      } else {
        setSuburbOptions([])
        form.setFieldsValue({
          suburb: '',
          state: ''
        })
      }
    } catch (error) {
      setSuburbOptions([]) // 请求失败时可以设置为空数组或处理其他错误逻辑
      console.error(error)
    }
  }

  const isObjectsEqual = (obj1:any, obj2:any) => {
  // 先比较 key 数量
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every(key => 
    Object.prototype.hasOwnProperty.call(obj2, key) && obj1[key] === obj2[key]
  );
}


  // 校验成功，发起提交请求
  const onFinish = (values: any) => {
    // 未变化时取消提交
    const isChanged = isObjectsEqual(values, initialValues)
    if (isChanged) {
      cancel()
      return
    }
    setSubmitLoading(true);
    if (type === 'edit') {
      saveAddress(values)
    } else if (type === 'add') {
      // 未登录走登录注册保存地址接口
      loginExpress(values)
    }
  }

  const loginExpress = async (values: any) => {
    try {
      if (!email) {
        throw new Error('The email field is required.')
      }
      const dataObj: any = {
        email: email,
        subscribed: subscriptionEmail ? 1 : 0,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        zoneID: '',
        address: values.address,
        address1: values.address1,
        suburb: values.suburb,
        state: values.state,
        postCode: values.postCode
      }
      // 通过suburb反查zoneID
      if (values.suburb) {
        const target = suburbOptions.filter(item => item.subName === values.suburb)

        // 给zoneID赋值
        if (target.length) {
          dataObj.zoneID = target[0].zoneID
        }
      }

      const res = await loginApi.loginExpress(dataObj)
      setSubmitLoading(false)
      if (res.data) {
        message.success('Success')
        success(res.data.addressID)

        // matomo记录注册结果
        const registerResult = 0
        matomoSetCustomDimension(27, {registerMethod: 'chkRegister', registerResult})
      }

    } catch (error: any) {
      if (error.code == 115003) {
        alert(error.message)
      } else {
        message.error(error.message)
      }
      setSubmitLoading(false)

      if (error.code) {
        // matomo记录注册结果
        const registerResult = error.code
        matomoSetCustomDimension(27, {registerMethod: 'chkRegister', registerResult})
      }
    }
  }

  // 提交新增/编辑地址
  const saveAddress = async (values: any) => {
    try {
      const dataObj: any = {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        zoneID: '',
        address: values.address,
        address1: values.address1,
        suburb: values.suburb,
        state: values.state,
        postCode: values.postCode,
        isDefaultBillingAddress: values.isDefaultBillingAddress ? 1 : 0,
        isDefaultAddress: values.isDefaultAddress ? 1 : 0
      }

      // 通过suburb反查zoneID
      if (values.suburb) {
        const target = suburbOptions.filter(item => item.subName === values.suburb)

        // 给zoneID赋值
        if (target.length) {
          dataObj.zoneID = target[0].zoneID
        }
      }

      // 判断新增还是编辑地址
      if (isEdit) dataObj.addressID = address?.addressID
      const params = isEdit ? myAccountApi.editAddresses : myAccountApi.addAddresses

      let res = await params(dataObj)
      setSubmitLoading(false)
      if (res.data) {
        message.success('Success')
        const { addressID } = res.data
        changeAddress([addressID]).then(res => {
          console.log(res, ' == res');
          if (
            (editType == 'shipping' && dataObj.isDefaultBillingAddress)
            || (editType == 'billing' && dataObj.isDefaultAddress)
          ) {
            // 需要两个地址同时都修改的，设置true属性
            success(addressID, true)
          } else {
            success(addressID)
          }
        })

        // 暂时紧急处理，手动发起一次change请求. 后续需要更改为接口直接实现change
        if (dataObj.isDefaultBillingAddress) {
          await myAccountApi.changeAddress({ addressID: Number(addressID), isBill: 1 })
        }
        if (dataObj.isDefaultAddress) {
          await myAccountApi.changeAddress({ addressID: Number(addressID), isBill: 0 })
        }
      }
    } catch (error: any) {
      message.error(error.message)
      setSubmitLoading(false)
    }
  }

  const rowSelection = {
    selectedRowKeys: selectedRowKeys,
    onChange: (key: React.Key[]) => {
      changeAddress(key, true)
      setSelectedRowKeys(key)
    }
  };

  const changeAddress = async (key: React.Key[], isSetAddress?: boolean) => {
    try {
      await myAccountApi.changeAddress({ addressID: key[0], isBill: editType === 'billing' ? 1 : 0 })
      if (!isSetAddress) return
      const addressItem = addressList?.find((item: any) => item.addressID === key[0])
      if (setAddressInfo) {
        switch (editType) {
          case 'billing':
            setAddressInfo(null, addressItem)
            break
          case 'shipping':
            setAddressInfo(addressItem, null)
            break
        }
        success(key[0])
      }
    } catch (error: any) {
      message.error(error.message)
    }
  }

  const addNewAddress = () => {
    setAddAddressType('add');
    setNeedBack(true);
    form.resetFields()
  }
  return (
    <div className={`${styles['address-form']} ${addAddressType === 'add' && styles['add']}`}>
      {
        addAddressType === 'add'
        &&
        <div className={styles['address-form-title']}>
          <div className={styles['title-text']}>Add New Shipping Address</div>
        </div>
      }

      {
        addAddressType === 'edit'
        &&
        <div className={styles['address-form-title']}>
          <div className={styles['title-text']}>{editType == "billing" ? 'Billing' : 'Shipping'}  Address</div>
          {
            addAddressType === 'edit'
            &&
            <div className={styles['select-address-box']} onClick={() => { setShowSelectAddress(!showSelectAddress) }}>
              Select From Address Book<UpOutlined className={`${styles['select-icon-down']} ${!showSelectAddress && styles['rotate']}`} />
            </div>
          }
        </div>
      }

      <div className={styles['from-tip']}>
        <WarningFilled style={{ color: '#FFCB2E', fontSize: '20px', marginRight: '8px' }} />Please use only numbers between 0-9 and characters between A-Z,a-z,.,-,&apos;,/ and &
      </div>
      <ConfigProvider
        theme={{
          components: {
            Input: {
              fontSize: 16,
              fontSizeLG: 16,
              fontSizeSM: 16
            }
          }
        }}
      >
        <Form
          name="basic"
          layout="vertical"
          size={'large'}
          style={{ width: '100%' }}
          form={form}
          initialValues={{}}
          onFinish={onFinish}
          autoComplete="off"
          scrollToFirstError
        >

          <div className={styles['form-box']}>
            <div className={styles['box-item']}>
              <Form.Item
                label="Recipient Name"
                rules={[{ required: true }]}
                style={{ marginBottom: 0 }}
                required
              >
                <Space size={16}>
                  <Form.Item
                    label=""
                    name="firstName"
                    rules={[
                      {
                        validator(_, value) {
                          if (!value) {
                            return Promise.reject(new Error(`First name is required`));
                          } else if (value.length < 2) {
                            return Promise.reject(new Error(`Please enter a value between 2 and 100 characters long.`));
                          } else if (value.length > 100) {
                            return Promise.reject(new Error(`The First Name length is no more than 100 characters.`));
                          }
                          return Promise.resolve()
                        },
                      },
                    ]}
                    style={{ display: 'block' }}
                  >
                    <Input maxLength={60} placeholder="First Name" />
                  </Form.Item>
                  <Form.Item
                    label=""
                    name="lastName"
                    style={{ display: 'block' }}
                    rules={[
                      {
                        validator(_, value) {
                          if (!value) {
                            return Promise.reject(new Error(`Last name is required`));
                          } else if (value.length < 2) {
                            return Promise.reject(new Error(`Please enter a value between 2 and 100 characters long.`));
                          } else if (value.length > 100) {
                            return Promise.reject(new Error(`The Last Name length is no more than 100 characters.`));
                          }
                          return Promise.resolve()
                        },
                      },
                    ]}
                  >
                    <Input maxLength={60} placeholder="Last Name" onChange={() => { form.validateFields(['firstName']); }} />
                  </Form.Item>
                </Space>
              </Form.Item>

              <div className={styles['post-code']}>
                <div className={styles['post-code-item']}>
                  <Form.Item
                    label="PostCode"
                    name="postCode"
                    rules={[
                      { required: true, message: `Postcode is required.` },
                      {
                        validator: async (_, value) => {
                          const regex = /^[0-9]+$/;
                          if (value && !regex.test(value)) {
                            return Promise.reject('Numbers only.');
                          }
                          if (value && value.length === 4) {
                            try {
                              let res = await myAccountApi.getSubAndStatByPostcode({ postCode: value })
                              if (res.data && res.data.length) {
                                return Promise.resolve();
                              } else {
                                return Promise.reject(new Error('Invalid Postcode.'));
                              }
                            } catch (error) {
                              return Promise.reject(new Error('Invalid Postcode.'));
                            }
                          } else if (value && value.length > 4) {
                            return Promise.reject(new Error('Please enter a value between 0 and 4 characters long'));
                          } else if (value && value.length < 4) {
                            return Promise.reject(new Error('Please enter a value between 0 and 4 characters long'));
                          }
                        }
                      }
                    ]}
                  >
                    <Input type={'tel'} maxLength={4} placeholder="PostCode" onChange={changePostCode} />
                  </Form.Item>
                </div>
                <div className={styles['post-code-item']}>
                  <Form.Item
                    label="Suburb"
                    name="suburb"
                    rules={[{ required: true, message: `Surburb is required.` }]}
                  >
                    <Select onChange={handleSuburbChange}>
                      {
                        !!(suburbOptions && suburbOptions.length) &&

                        suburbOptions.map((item: suburbOptionsItem) => (
                          <Option value={item.subName} key={item.subID}>{item.subName}</Option>
                        ))
                      }
                    </Select>
                  </Form.Item>
                </div>
              </div>

              <Form.Item
                label="Street Address"
                name="address"
                rules={[{ required: true, message: `Address Line is required.` },]}
              >
                <Input maxLength={200} style={{ width: '100%' }} />
              </Form.Item>

              {
                type == "edit"
                &&
                <>
                  <Form.Item
                    name="isDefaultAddress"
                    valuePropName="checked"
                    style={{ marginBottom: '0' }}
                  >
                    <Checkbox>Set as Default shipping address</Checkbox>
                  </Form.Item>

                  <Form.Item
                    name="isDefaultBillingAddress"
                    valuePropName="checked"
                  >
                    <Checkbox>Use this address for billing address</Checkbox>
                  </Form.Item>
                </>
              }

            </div>

            <div className={styles['box-item']}>
              <Form.Item
                label="Phone Number (Example:04xxxxxxxx/03xxxxxxxx)"
                name="phone"
                rules={[
                  { required: true, message: `Phone/Mobile is required.` },
                  {
                    validator(_, value) {
                      if (value && !value.match(/^[0-9]+$/)) {
                        return Promise.reject(new Error('Numbers only'));
                      } else if (value && value.length != 10) {
                        return Promise.reject(new Error('Your phone number should be 10 characters long.'));
                      } else if (value && !value.match(/^[0][234578][0-9]{8}$/)) {
                        return Promise.reject(new Error('Invalid phone number.'));
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
                getValueFromEvent={(e: any) => {
                  return e.target.value.replace(/\s+/g, '')
                }}
              >
                <Input
                  type="tel"
                  // maxLength={10}
                  placeholder="For delivery use only"
                />
              </Form.Item>

              <div className={styles['post-code']}>
                <div className={styles['post-code-item']}>
                  <Form.Item
                    label="State"
                    name="state"
                    rules={[{ required: true, message: `State is required.` },]}
                  >
                    <Input disabled />
                  </Form.Item>
                </div>
                <div className={styles['post-code-item']}>
                  <Form.Item label="Country" name="country">
                    <Input placeholder="Australia" disabled />
                  </Form.Item>
                </div>
              </div>

              <Form.Item
                label="Address line 2 (optional)"
                name="address1"
              >
                <Input maxLength={200} />
              </Form.Item>
            </div>
          </div>


          {/* 提交按钮 */}
          <Form.Item>
            <div className={styles['btn-box']}>
              {
                addAddressType === 'edit'
                  ?
                  <>
                    <Button className={styles['cancel-btn']} onClick={cancel}>
                      Cancel
                    </Button>
                    <Button className={styles['save-btn']} htmlType="submit" loading={submitLoading}>
                      Update Address
                    </Button>
                  </>
                  :
                  <div>
                    {
                      needBack
                      &&
                      <Button className={styles['cancel-btn']} onClick={() => { cancel(); setNeedBack(false) }} ghost>
                        Back
                      </Button>
                    }
                    <Button className={styles['save-btn']} htmlType="submit" disabled={!canEdit} loading={submitLoading}>
                      Use This Address
                    </Button><br />
                    <p style={{ color: '#D70B17', textAlign: 'center', marginTop: '8px' }}>* Use address to complete your order</p>
                  </div>
              }
            </div>
          </Form.Item>
        </Form>
      </ConfigProvider>

      {/* 选择地址弹窗 */}
      {
        addAddressType === 'edit'
        &&
        <div className={`${styles['address-select']} ${showSelectAddress ? styles['show'] : styles['hide']}`}>
          <Table
            className={styles['address-table']}
            rowSelection={{
              type: "radio",
              ...rowSelection,
            }}
            columns={columns}
            // onRow={(record) => {
            //   return {
            //     onClick: () => {
            //       const key = [record.addressID]
            //       changeAddress(key, true)
            //       setSelectedRowKeys(key)
            //     }
            //   };
            // }}
            dataSource={addressList}
            pagination={false}
            rowKey="addressID"
          />
          <Button type="primary" className={styles['address-add-btn']} icon={<PlusOutlined />} onClick={addNewAddress}>Add New Address</Button>
        </div>
      }
      {
        !canEdit
        &&
        <div className={styles['opacity-modal']}></div>
      }
    </div>
  )
}
